import React, { useState } from 'react';
import AccountsTabs, { ACCOUNT_TABS, useAccountTabs } from './AccountsTabs';
// The Dash tab is a board the rep composes (docs/ACCOUNT-BOARD.md) — the fixed
// stack AccountsDashTab used to render is now one widget per panel, and the
// other tabs became the "open the full view" targets behind each header.
import AccountsBoard from './board/AccountsBoard';
import { ACCOUNT_BOARDS } from '@techstack/core';
import AccountsBcpsTab from './AccountsBcpsTab';
import AccountsPeopleSplit from './AccountsPeopleSplit';
import AccountsSection from './AccountsSection';
import AccountsPyramidTab from './AccountsPyramidTab';
import AccountsTrapsTab from './AccountsTrapsTab';
import AccountsTechmapTab from './AccountsTechmapTab';
import AccountsLocationsTab from './AccountsLocationsTab';
// The account-scoped news feed TekStak already ships on the workspace —
// reused rather than reimplemented, so both surfaces stay one component.
import AccountNewsSection from '../AccountWorkspace/AccountNewsSection';
import AccountsSolicitationsTab from './AccountsSolicitationsTab';
import AccountNotesSection from '../AccountWorkspace/AccountNotesSection';

/*
 * AccountDashboard — per-account dashboard for /accounts, mirroring the
 * GAS AccountDashboard anatomy (apps/gas App.jsx): identity header +
 * horizontal tab strip + one panel per tab. Panels either mount existing
 * components (OrgChart, AccountNotesSection, ZeroTrust, TechMap) or are
 * TekStak-idiomatic ports of the GAS panels (P5b: BCP's, Value pyramid,
 * Trap questions, Competitive, Tech map).
 */

/*
 * Which tabs ARE boards. A tab used to be a fixed panel and the Dash was the
 * one exception; the construct is the same for both, so the exception is now
 * the registry — add a board in @techstack/core and its tab composes itself.
 */
const BOARD_BY_TAB = Object.keys(ACCOUNT_BOARDS).reduce((acc, key) => {
  acc[ACCOUNT_BOARDS[key].tab] = key;
  return acc;
}, {});

/*
 * People is NOT composed as a board, and the exception is deliberate (Ryan,
 * 2026-09-18). A board stacks its widgets, and the org chart is a pan/zoom
 * canvas: stacked above the directory it ate the wheel, so reaching the
 * directory meant fighting it — and reordering the stack only moves which one
 * does the eating. The two belong side by side, which is a layout a grid of
 * independently-sized cells cannot express.
 *
 * The board REGISTRY still carries `people` — GAS composes its own People tab
 * from it, and deleting the entry would empty that tab. This is a TekStak
 * routing decision, not a change to the shared catalogue.
 */
delete BOARD_BY_TAB.org;

export default function AccountDashboard({ selectedDepartment, departments }) {
  const [activeKey, setTab] = useAccountTabs();
  // The Dash board's toolbar renders into the tab strip's trailing slot: the
  // strip has the room, and a 32px band holding one "Customize" button did
  // not earn its place above the first widget.
  const [toolbarHost, setToolbarHost] = useState(null);
  const activeTab = ACCOUNT_TABS.find(t => t.key === activeKey) || ACCOUNT_TABS[0];
  const boardKey = BOARD_BY_TAB[activeKey] || null;

  const notesAccount = selectedDepartment?.acronym || String(selectedDepartment?.department_id || '');

  return (
    <div className="accounts-dashboard">
      {/*
        * NO SECOND HEADER.
        *
        * This was a logo, the agency's name and "Dept #7760" sitting directly
        * under a master header that already said DGS, Department of General
        * Services and #7760. Measured on an 893px viewport, the chrome above
        * the first widget came to 329px — 37% of the screen — with the account
        * name in three of six bands.
        *
        * The mark moved up to the filter bar (TopNav) and the facts moved
        * behind the (i) next to the name, where there is room for more of them
        * than this band ever showed. 83px back, nothing lost.
        */}
      <AccountsTabs
        activeKey={activeKey}
        onChange={setTab}
        trailing={boardKey ? <div ref={setToolbarHost} /> : null}
      />

      <div className="accounts-panel" role="tabpanel" aria-label={activeTab.label}>
        {boardKey && (
          <AccountsBoard
            // Remount on a board switch: the two hold different arrangements
            // and different storage, and RGL keeps per-instance layout state.
            key={boardKey}
            board={boardKey}
            selectedDepartment={selectedDepartment}
            departments={departments}
            onOpenTab={setTab}
            toolbarInto={toolbarHost}
          />
        )}
        {activeKey === 'org' && (
          <AccountsSection title="People">
            <AccountsPeopleSplit selectedDepartment={selectedDepartment} />
          </AccountsSection>
        )}
        {/*
          * Wrapped HERE, not inside the components: both are also mounted as
          * board widgets (which bring their own .abw chrome) and on
          * /workspace, so wrapping inside would double the title on two other
          * surfaces. `hideHeader` drops the component's own heading because
          * the section now provides it — the same trade the board makes with
          * `compact`.
          */}
        {activeKey === 'notes' && (
          <AccountsSection title="Notes">
            <AccountNotesSection account={notesAccount} hideHeader />
          </AccountsSection>
        )}
        {activeKey === 'news' && (
          <AccountsSection title="News">
            <AccountNewsSection account={selectedDepartment?.acronym || selectedDepartment?.official_name} />
          </AccountsSection>
        )}
        {activeKey === 'budget' && (
          <AccountsSection title="BCP’s">
            <AccountsBcpsTab selectedDepartment={selectedDepartment} />
          </AccountsSection>
        )}
        {activeKey === 'pyramid' && (
          <AccountsSection title="Value pyramid">
            <AccountsPyramidTab selectedDepartment={selectedDepartment} />
          </AccountsSection>
        )}
        {activeKey === 'traps' && (
          <AccountsSection title="Trap questions">
            <AccountsTrapsTab selectedDepartment={selectedDepartment} />
          </AccountsSection>
        )}
        {activeKey === 'solicitations' && (
          <AccountsSection title="Solicitations">
            <AccountsSolicitationsTab selectedDepartment={selectedDepartment} />
          </AccountsSection>
        )}
        {activeKey === 'techmap' && (
          <AccountsSection title="Tech map">
            <AccountsTechmapTab selectedDepartment={selectedDepartment} />
          </AccountsSection>
        )}
        {activeKey === 'locations' && (
          <AccountsSection title="Locations">
            <AccountsLocationsTab selectedDepartment={selectedDepartment} onOpenTab={setTab} />
          </AccountsSection>
        )}
      </div>
    </div>
  );
}
